import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import DashboardShell from "@/components/DashboardShell";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr, mediaUrl } from "@/lib/api";
import { themeOf } from "@/lib/themes";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, MoreHorizontal, Eye, Copy, Trash2, Pencil, Sparkles } from "lucide-react";

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
};

const TimelinePreview = ({ tl }) => {
  const t = themeOf(tl.theme);
  return (
    <div className="h-36 relative overflow-hidden" style={{ background: t.bgGradient }}>
      {tl.cover_image ? (
        <img src={mediaUrl(tl.cover_image)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90" loading="lazy" />
      ) : (
        <div className="absolute inset-0 flex items-center pl-6">
          <div className="relative pl-5">
            <div className="absolute left-0 top-1 bottom-1 w-[2px]" style={{ background: t.line }} />
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-2 mb-2.5">
                <span className="h-2 w-2 rounded-full -ml-[3px]" style={{ background: t.dot }} />
                <span className="h-1.5 rounded-full" style={{ width: 60 - i * 12, background: `${t.accent}99` }} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const StatusBadge = ({ status }) => (
  <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${status === "published" ? "bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400" : "bg-muted text-muted-foreground"}`}>
    {status === "published" ? "Published" : "Draft"}
  </span>
);

const Card = ({ tl, onDelete, onDuplicate }) => {
  const nav = useNavigate();
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className="group rounded-2xl border border-border bg-card overflow-hidden transition-transform duration-300 hover:-translate-y-1">
      <Link to={`/editor/${tl.id}`}><TimelinePreview tl={tl} /></Link>
      <div className="p-5">
        <div className="flex items-start justify-between gap-2">
          <Link to={`/editor/${tl.id}`} className="min-w-0">
            <h3 className="font-display font-semibold text-foreground truncate">{tl.title}</h3>
            <div className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground">
              <StatusBadge status={tl.status} /> · {tl.events?.length || 0} events · {tl.views || 0} views
            </div>
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger data-testid={`card-menu-${tl.id}`} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-muted text-muted-foreground shrink-0"><MoreHorizontal size={18} /></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => nav(`/editor/${tl.id}`)}><Pencil size={14} className="mr-2" /> Edit</DropdownMenuItem>
              {tl.status === "published" && <DropdownMenuItem onClick={() => window.open(`/t/${tl.creator_username}/${tl.slug}`, "_blank")}><Eye size={14} className="mr-2" /> View public</DropdownMenuItem>}
              <DropdownMenuItem onClick={() => onDuplicate(tl)}><Copy size={14} className="mr-2" /> Duplicate</DropdownMenuItem>
              <AlertDialog>
                <AlertDialogTrigger asChild><DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-destructive"><Trash2 size={14} className="mr-2" /> Delete</DropdownMenuItem></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Delete “{tl.title}”?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => onDelete(tl)} className="bg-destructive text-destructive-foreground" data-testid={`confirm-delete-${tl.id}`}>Delete</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </motion.div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("all");

  const load = async () => {
    try { const { data } = await api.get("/timelines"); setItems(data); }
    catch (e) { toast.error(apiErr(e)); setItems([]); }
  };
  useEffect(() => { load(); }, []);

  const onDelete = async (tl) => {
    try { await api.delete(`/timelines/${tl.id}`); setItems((p) => p.filter((x) => x.id !== tl.id)); toast.success("Deleted"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const onDuplicate = async (tl) => {
    try { const { data } = await api.post(`/timelines/${tl.id}/duplicate`); setItems((p) => [data, ...p]); toast.success("Duplicated"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const filtered = (items || []).filter((t) => tab === "all" ? true : tab === "drafts" ? t.status !== "published" : tab === "shared" ? t.is_owner === false : t.status === "published");

  return (
    <DashboardShell>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground">{greeting()}, {user?.name?.split(" ")[0]}.</h1>
          <p className="mt-2 text-muted-foreground">Pick up where you left off, or start something new.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/create?mode=ai"><Button variant="outline" className="rounded-full" data-testid="dash-ai-btn"><Sparkles size={16} className="mr-1" /> Create with AI</Button></Link>
          <Link to="/create"><Button className="rounded-full" data-testid="dash-create-btn"><Plus size={16} className="mr-1" /> Create Timeline</Button></Link>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-6">
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
          <TabsTrigger value="drafts" data-testid="tab-drafts">Drafts</TabsTrigger>
          <TabsTrigger value="published" data-testid="tab-published">Published</TabsTrigger>
          <TabsTrigger value="shared" data-testid="tab-shared">Shared with me</TabsTrigger>
        </TabsList>
      </Tabs>

      {items === null ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">{[0,1,2].map(i => <Skeleton key={i} className="h-60 rounded-2xl" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-16 text-center">
          <div className="mx-auto mb-4 h-14 w-14 rounded-2xl bg-accent grid place-items-center text-primary"><Plus size={26} /></div>
          <h3 className="font-display text-xl font-semibold text-foreground">No timelines yet</h3>
          <p className="mt-1 text-muted-foreground">Create your first timeline and publish it in minutes.</p>
          <Link to="/create"><Button className="mt-6 rounded-full" data-testid="empty-create-btn">Create Timeline</Button></Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="timeline-grid">
          {filtered.map((tl) => <Card key={tl.id} tl={tl} onDelete={onDelete} onDuplicate={onDuplicate} />)}
        </div>
      )}
    </DashboardShell>
  );
}
